/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { addEdge, removeElements } from "react-flow-renderer";
import { NodeStyle } from "./style";
import { StarFilled, TeamOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { formatClassname } from 'datastake-daf/src/helpers/ClassesHelper';

export const types = {
  STAKEHOLDER: 'STAKEHOLDERS',
  BENEFICIARIES: 'BENEFICIARIES',
  OWN_DATA: 'OWN_DATA',
};

const pushTypes = {
  LEFT: 'LEFT',
  RIGHT: 'RIGHT',
};

const OwnData = ({
  el,
  t,
  featuredData,
  supplyChainActor,
  otherInformationSources,
  onClick = () => { },
  isActive = false,
  getOption = () => '',
}) => {
  const header = useMemo(() => {
    if (el.config && el.config.ownDataType) {
      return getOption('stakeholderSubCategoriesOptions', el.config.ownDataType);
    }
    return t('Unknown');
  }, [el, getOption, t]);

  return (
    <NodeStyle
      onClick={onClick}
      className={formatClassname(['own-data', isActive && 'is-active'])}
    >
      <div className="own-data-header">
        <div className="left" />
        <div className="right">
          <div className="info">
            <label className="legal-form" style={{ color: 'red', flex: 1 }}>
              {header}
            </label>
            <label className="id">
              {el.id}
            </label>
          </div>
          <label className="subject-label">{el.label}</label>
        </div>
      </div>
      <div className="sources">
        {(featuredData || !el.config.sources) && <StarFilled />}
        {supplyChainActor && <div className="circle supply-chain-actor" />}
        {otherInformationSources && <div className="circle other-info-source" />}
        <label>{el.config.sources ? el.config.sources.length : 1} {t('source(s)')}</label>
      </div>
    </NodeStyle>
  );
};

const Default = ({
  featuredData,
  supplyChainActor,
  otherInformationSources,
  isStakeholder = false,
  el,
  t,
  onClick = () => { },
  isActive,
  isCooperative,
}) => (
  <NodeStyle
    className={formatClassname(['default', isActive && 'is-active'])}
    onClick={onClick}
  >
    <div className="default-header">
      <div className="info">
        <div className="type">
          {isStakeholder ? (
            <>
              <TeamOutlined />
              <label>{t(isCooperative ? 'Founding Member' : 'Shareholder')}</label>
            </>
          ) : (
            <>
              <UserSwitchOutlined />
              <label>{t('Beneficiary')}</label>
            </>
          )}
        </div>
        <label className="id">
          {el.config.itemId}
        </label>
      </div>
      <label className="subject-label">{el.label}</label>
    </div>
    <div className="sources">
      {(featuredData || !el.config.sources) && <StarFilled />}
      {supplyChainActor && <div className="circle supply-chain-actor" />}
      {otherInformationSources && <div className="circle other-info-source" />}
      <label>{el.config.sources ? el.config.sources.length : 1} {t('source(s)')}</label>
    </div>
  </NodeStyle>
);

export const ELEMENTS_TO_KEEP = [
  {
    id: 'a',
    type: 'input',
    data: {
      label: (
        <>
        </>
      ),
    },
    position: {
      x: 74, y: 63,
    },
    style: {
      width: 1,
      padding: 0,
      height: 10000,
      borderColor: '#CCCCCC',
    }
  },
  {
    id: 'b',
    type: 'input',
    data: {
      label: (
        <></>
      ),
    },
    position: {
      x: 74, y: -10000,
    },
    style: {
      width: 1,
      padding: 0,
      height: 10000,
      borderColor: '#CCCCCC',
    },
  },
];

export const DEFAULT_NODE_WIDTH = 200;
export const DEFAULT_NODE_HEIGHT = 60;
export const GAP = 10;
export const DEFAULT_ZOOM = 1;
export const MIN_ZOOM = 0.5;
let incrementer = 1;

export const getZoom = (zoom, minX, maxX, minY, maxY, contWidth, contHeight, setCenter, zoomTo) => {
  if (zoom < MIN_ZOOM) {
    setCenter(75, 20);
    zoomTo(zoom);
    return false;
  }

  if ((maxX - (minX - DEFAULT_NODE_WIDTH) + (GAP * 2)) * zoom > contWidth) {
    return getZoom(zoom - 0.1, minX, maxX, minY, maxY, contWidth, contHeight, setCenter, zoomTo);
  }
  if ((maxY - (minY - DEFAULT_NODE_HEIGHT) + (GAP * 2)) * zoom > contHeight) {

    return getZoom(zoom - 0.1, minX, maxX, minY, maxY, contWidth, contHeight, setCenter, zoomTo);
  }

  return zoom;
}

const initializeData = (data, firstParentId, type = types.STAKEHOLDER) => {
  const items = [];
  const levelsInc = type === types.STAKEHOLDER ? -1 : 1;
  const initialLevel = type === types.STAKEHOLDER ? -1 : 1;

  const addItem = (d, level, inheritId) => {
    const id = `${d.id}-${incrementer}`;
    items.push({
      id,
      itemId: d.id,
      label: d.label,
      level,
      parentId: type === types.STAKEHOLDER ? inheritId : undefined,
      childId: type === types.BENEFICIARIES ? inheritId : undefined,
      type: type,
      sources: d.sources,
      data: d.data,
      otherSources: d.otherSources || [],
    });
    incrementer++;
    return id;
  }

  const addChildren = (children, level, inheritId) => {
    children.forEach((d) => {
      const id = addItem(d, level, inheritId);
      if (d.children && d.children.length) {
        addChildren(d.children, level + levelsInc, id);
      }
    });
  }

  data.forEach((d) => {
    const id = addItem(d, initialLevel, firstParentId);
    if (d.children && d.children.length) {
      addChildren(d.children, initialLevel + levelsInc, id);
    }
  });

  const sections = [];
  const levels = items.map((i) => i.level);
  const min = Math.min(...levels);
  const max = Math.max(...levels);

  for (let i = min; i <= max; i += 1) {
    const section = items.filter((item) => item.level === i);
    sections.push(section);
  }

  return {
    sections,
    min,
    max,
  };
};

const push = (maxX, filtered, type = pushTypes.LEFT, minY = 0, maxY = 0, ihnerit = [], callback) => {
  const initial = type === pushTypes.LEFT ? -300 : 300;
  const X = initial + maxX;

  if (filtered.length % 2 !== 0) {
    const middle = (filtered.length - 1) / 2;
    const initDecr = ((50) / (filtered.length - 1) * 2);
    const decr = type === pushTypes.LEFT ? initDecr : initDecr * -1;
    let topX = type === pushTypes.LEFT ? X + 50 : X - 50;
    let topY = -100 * ((filtered.length - 1) / 2);
    let bottomX = X + decr;
    let bottomY = 100;
    let yDecr = 100;
    let middle1Y = 0;
    if (ihnerit.length < 3) {
      yDecr = 80;
      middle1Y = (maxY + minY) / 2;
      topY = middle1Y - (yDecr * ((filtered.length - 1) / 2));
      bottomY = middle1Y + yDecr;
    } else if (topY > minY) {
      topY = minY - 40;
      const dif = (maxY - minY) / (filtered.length);
      yDecr = dif + 40;
      bottomY = yDecr;
    }
    callback({
      ...filtered[middle],
      position: {
        x: X,
        y: middle1Y,
      },
    });
    filtered.forEach((f, i) => {
      if (i !== middle) {
        if (i < middle) {
          callback({
            ...f,
            position: {
              x: topX,
              y: topY,
            },
          });
          topX -= decr;
          topY += yDecr;
        } else {
          callback({
            ...f,
            position: {
              x: bottomX,
              y: bottomY,
            },
          });
          bottomX += decr;
          bottomY += yDecr;
        }
      }
    });
  } else {
    const middleY = (maxY + minY) / 2;
    const initDecr = (50 / (filtered.length - 2) * 2);
    const decr = type === pushTypes.LEFT ? initDecr : initDecr * -1;
    let topX = type === pushTypes.LEFT ? X + 50 : X - 50;
    let topY = -100 * ((filtered.length - 2) / 2) - 50;
    let bottomX = X + decr;
    let bottomY = 100 + 50;
    let yDecr = 100;
    if (topY >= minY) {
      topY = minY - 40;
      const dif = (maxY - minY) / (filtered.length - 2);
      yDecr = dif + 40;
      bottomY = yDecr;
    }
    let middle1Y;
    let middle2Y;
    if (ihnerit.length < 3) {
      yDecr = 80;
      middle1Y = middleY - 50;
      middle2Y = middleY + 50;
      topY = middle1Y - (yDecr * ((filtered.length - 2) / 2));
      bottomY = middle2Y + yDecr;
    } else {
      middle1Y = -50;
      middle2Y = 50;
    }
    const middle = filtered.length / 2;
    callback({
      ...filtered[middle - 1],
      position: {
        x: X,
        y: middle1Y,
      },
    });
    callback({
      ...filtered[middle],
      position: {
        x: X,
        y: middle2Y,
      },
    });
    filtered.forEach((f, j) => {
      const i = j + 1;
      if (i !== middle && i !== middle + 1) {
        if (i < middle) {
          callback({
            ...f,
            position: {
              x: topX,
              y: topY,
            },
          });
          topX -= decr;
          topY += yDecr;
        } else {
          callback({
            ...f,
            position: {
              x: bottomX,
              y: bottomY,
            },
          });
          bottomX += decr;
          bottomY += yDecr;
        }
      }
    });
  }
}

const getXVal = (p) => {
  if (p > 0) {
    return 200;
  } else {
    return -200;
  }
}

const getYVal = (p) => {
  if (p > 0) {
    return 40;
  } else {
    return -40;
  }
}

const pushLessThan3 = (filtered, elements) => {
  if (filtered.length === 2) {
    const first = filtered[0];
    const second = filtered[1];
    if (first && second) {
      const firstParent = first.config.parentId ?
        elements.find((e) => e.id === first.config.parentId)
        : elements.find((e) => e.id === first.config.childId);
      const secondParent = first.config.parentId ?
        elements.find((e) => e.id === second.config.parentId)
        : elements.find((e) => e.id === second.config.childId);
      const fpX = firstParent.position.x;
      const fpY = firstParent.position.y;
      const spX = secondParent.position.x;
      const spY = secondParent.position.y;

      if (firstParent.id === secondParent.id) {
        elements.push({
          ...first,
          position: {
            x: fpX + getXVal(fpX),
            y: fpY + getYVal(fpY),
          }
        });
        elements.push({
          ...second,
          position: {
            x: fpX + getXVal(fpX),
            y: fpY - getYVal(fpY),
          }
        });
      } else {
        elements.push({
          ...first,
          position: {
            x: fpX + getXVal(fpX),
            y: fpY + getYVal(fpY),
          }
        });
        elements.push({
          ...second,
          position: {
            x: spX + getXVal(spX),
            y: spY + getYVal(spY),
          }
        });
      }
    }
  } else {
    filtered.forEach((el) => {
      const parent = el.config.parentId ?
        elements.find((e) => e.id === el.config.parentId)
        : elements.find((e) => e.id === el.config.childId);
      if (parent) {
        elements.push({
          ...el,
          position: {
            x: parent.position.x + getXVal(parent.position.x),
            y: parent.position.y + getYVal(parent.position.y),
          }
        })
      }
    });
  }
}

const getSources = (sources, el) => {
  const toReturn = {
    featuredData: false,
    supplyChainActor: false,
    otherInformationSources: false,
  };
  Object.keys(toReturn).forEach((k) => {
    if (sources && sources[k]) {
      sources[k].forEach((s) => {
        if (el.config.sources && Array.isArray(el.config.sources)) {
          el.config.sources.forEach((i) => {
            if (s.id === i) {
              toReturn[k] = true;
              return;
            }
          })
        }
      });
    }
  });
  return toReturn;
};

const allHaveMaxOneChild = (elements) => {
  let isValid = true;
  elements.forEach((el) => {
    if (el.config.childId) {
      if (elements.find((e) => e.config.childId === el.config.childId && e.id !== el.id)) {
        isValid = false;
      }
    }
  });
  return isValid;
};

export function useNodes(
  {
    stakeholders = null,
    beneficiaries = null,
    ownData = null,
    sources = null,
    t = (s) => s,
    getOption = () => '',
  }) {
  const [initElements, setInitElements] = useState();
  const [elements, setElements] = useState([]);
  const [activeNode, setActiveNode] = useState(null);
  const isCooperative = ownData?.data?.legalForm === 'cooperative';

  const stakeholdersData = useMemo(() =>
    (stakeholders && stakeholders.length && ownData) ? initializeData(stakeholders, ownData.id, types.STAKEHOLDER) : null, [stakeholders, ownData]);

  const beneficiariesData = useMemo(() =>
    (beneficiaries && beneficiaries.length && ownData) ? initializeData(beneficiaries, ownData.id, types.BENEFICIARIES) : null, [beneficiaries, ownData]);

  const items = useMemo(() => {
    const all = [];
    if (stakeholdersData) {
      all.push(...stakeholdersData.sections || [])
    }
    if (ownData) {
      all.push([{
        id: ownData.id,
        itemId: ownData.itemId,
        label: ownData.label,
        level: 0,
        type: types.OWN_DATA,
        sources: ownData.sources,
        ownDataType: ownData.type,
        data: ownData.data,
        otherSources: ownData.otherSources,
      }]);
    }
    if (beneficiariesData) {
      all.push(...beneficiariesData.sections || []);
    }
    return all;
  }, [ownData, beneficiariesData, stakeholdersData]);

  const onElementsRemove = (elementsToRemove) =>
    setElements((els) => removeElements(elementsToRemove, els));

  const onConnect = (params) => setElements((els) => addEdge({
    ...params, type: 'buttonedge',
  }, els));

  const setAllPositions = (nodes) => {
    const leftItems = nodes.filter((n) => n.config.level < 0);
    const rightItems = nodes.filter((n) => n.config.level > 0);
    const centerItem = nodes.find((n) => n.config.level === 0);
    const elements = [];

    if (centerItem) {
      elements.push({
        ...centerItem,
        position: { x: 0, y: 0 },
      });
      if (stakeholdersData) {
        for (let i = stakeholdersData.max; i >= stakeholdersData.min; i -= 1) {
          const filtered = leftItems.filter((n) => n.config.level === i);
          if (i === stakeholdersData.max) {
            push(0, filtered, pushTypes.LEFT, 0, 100, [1, 2, 3], (a) => elements.push(a));
          } else {
            const ihnerit = elements.filter((n) => n.config.level === i + 1);
            const exEs = ihnerit.map((i) => i.position.x);
            const maxX = Math.max(...exEs);
            const yS = ihnerit.map((i) => i.position.y);
            const maxY = Math.max(...yS);
            const minY = Math.min(...yS);
            if (filtered.length < 3) {
              pushLessThan3(filtered, elements);
            } else {
              push(maxX, filtered, pushTypes.LEFT, minY, maxY, ihnerit, (a) => elements.push(a));
            }
          }
        }
      }
      if (beneficiariesData) {
        for (let i = beneficiariesData.min; i <= beneficiariesData.max; i += 1) {
          const filtered = rightItems.filter((n) => n.config.level === i);
          if (i === beneficiariesData.min) {
            push(0, filtered, pushTypes.RIGHT, 0, 100, [1, 2, 3], (a) => elements.push(a));
          } else {
            const ihnerit = elements.filter((n) => n.config.level === i - 1);
            const exEs = ihnerit.map((i) => i.position.x);
            const yS = ihnerit.map((i) => i.position.y);
            const maxY = Math.max(...yS);
            const minY = Math.min(...yS);
            const maxX = Math.max(...exEs);
            if (filtered.length < 3 || allHaveMaxOneChild(filtered)) {
              pushLessThan3(filtered, elements);
            } else {
              push(maxX, filtered, pushTypes.RIGHT, minY, maxY, ihnerit, (a) => elements.push(a));
            }
          }
        }
      }
    }

    return elements;
  };

  const initializeItems = () => {
    let nodes = [];
    const con = [];
    const data = items;

    data.forEach((items) => {
      items.forEach((item) => {
        if (typeof item.parentId === 'string') {
          con.push({
            target: item.parentId,
            source: item.id,
          });
        } else if (typeof item.childId === 'string') {
          con.push({
            target: item.id,
            source: item.childId,
          });
        }
        nodes.push({
          id: item.id,
          sourcePosition: 'left',
          type: 'node',
          config: {
            ...item,
          },
          label: item.label,
        });
      });
    });

    let edges = [];
    con.forEach(({ source, target }, i) => {
      edges.push({
        id: `${source}-${target}-${i}`,
        source,
        target,
        type: 'edge',
      });
    });

    nodes = setAllPositions(nodes);

    setInitElements([
      ...nodes,
      ...edges,
    ]);
  };

  useEffect(() => {
    if (initElements) {
      setElements(initElements.map((el, i) => {
        switch (el.type) {
          case 'edge':
            return {
              id: el.id,
              source: el.source,
              target: el.target,
              style: {
                stroke: '#113366',
                strokeWidth: 2,
              }
            };
          case 'node':
            const {
              featuredData,
              supplyChainActor,
              otherInformationSources,
            } = getSources(sources, el);
            return {
              id: el.id,
              data: {
                label: (
                  <>
                    {el.config.type === types.OWN_DATA && (
                      <OwnData
                        getOption={getOption}
                        featuredData={featuredData}
                        supplyChainActor={supplyChainActor}
                        otherInformationSources={otherInformationSources}
                        t={t}
                        el={el}
                        onClick={() => setActiveNode(el)}
                        isActive={activeNode ? activeNode.id === el.id : false}
                      />
                    )}
                    {el.config.type === types.STAKEHOLDER && (
                      <Default
                        isCooperative={isCooperative}
                        isStakeholder={true}
                        featuredData={featuredData}
                        supplyChainActor={supplyChainActor}
                        otherInformationSources={otherInformationSources}
                        t={t}
                        el={el}
                        onClick={() => setActiveNode(el)}
                        isActive={activeNode ? activeNode.id === el.id : false}
                      />
                    )}
                    {el.config.type === types.BENEFICIARIES && (
                      <Default
                        featuredData={featuredData}
                        supplyChainActor={supplyChainActor}
                        otherInformationSources={otherInformationSources}
                        t={t}
                        el={el}
                        onClick={() => setActiveNode(el)}
                        isActive={activeNode ? activeNode.id === el.id : false}
                      />
                    )}
                  </>
                ),
              },
              className: 'node',
              position: el.position,
              sourcePosition: 'right',
              targetPosition: 'left',
              style: {
                width: 180,
              }
            };
          default:
            return null;
        }
      }));
    }
  }, [initElements, activeNode]);

  useEffect(() => {
    if (items) {
      initializeItems();
    }
  }, [items]);

  return {
    elements,
    initElements,
    onElementsRemove,
    onConnect,
    stakeholdersData,
    beneficiariesData,
    setActiveNode,
    activeNode,
    isCooperative,
  };
}
